import React, { useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import NextLink from 'next/link';
import { QueryClient, useQuery } from 'react-query';
import * as z from 'zod';
import {
	Avatar,
	Box,
	Button,
	Container, Dialog, DialogActions, DialogContent, DialogTitle,
	Grid,
	Hidden,
	Link,
	makeStyles,
	Typography,
	withStyles,
} from '@material-ui/core';
import { add, format, startOfToday } from 'date-fns';
import { dehydrate } from 'react-query/hydration';
import axios from 'axios';

import type { GetServerSideProps, NextPage } from 'next';
import type { QueryFunction } from 'react-query/types/core/types';

const apiUrl = process.env.NEXT_PUBLIC_API_URL !== undefined
	? process.env.NEXT_PUBLIC_API_URL
	: 'https://sandbox.qerko.com';

const querySchema = z.object({
	restaurantId: z.string(),
	date: z.string(),
}).nonstrict();

const useMenuRowStyles = makeStyles((theme) => ({
	title: {
		fontSize: '16px',
		fontWeight: 'lighter',
	},
	description: {
		color: '#6E8781',
		lineHeight: '22px',
		marginTop: theme.spacing(0.5),
		marginBottom: theme.spacing(0.5),
		fontSize: '14px',
	},
	price: {
		fontSize: '16px',
	},
}));

type Props = {date: string; delivery: string; restaurantId: string};

const MenuPage: NextPage<Props> = (p: { date?: string; restaurantId?: string; userAgent?: string }) => {
	const router = useRouter();
	const { date = p.date, restaurantId = p.restaurantId } = router.query;
	const props = querySchema.safeParse({ date, restaurantId });
	const isMobile = checkMobile(p.userAgent);

	if (!props.success) {
		return (
			<>
				<h2>CHYBNÉ PARAMETRY</h2>
				<pre>{props.error.message}</pre>
			</>
		);
	}

	return (
		<>
			{date && restaurantId && <MenuRequest
				restaurantId={props.data.restaurantId}
				date={props.data.date}
				isMobile={isMobile}
			/>}
		</>
	);
};

const getMenu: QueryFunction<any, [unknown, { restaurantId: string; date: string }]> = async ({ queryKey: [ , { restaurantId, date } ] }) => {
	const result = await axios.get(`${apiUrl}/api/v1/restaurant-admin/menu/menu-data-for-pdf?restaurantId=${restaurantId}&from=${date}&to=${date}&useDailyMenu=true&usePermanentMenu=true`);
	return result.data;
};

const getRestaurant: QueryFunction<any, [unknown, { restaurantId: string }]> = async ({ queryKey: [ , { restaurantId } ] }) => {
	const result = await axios.get(`${apiUrl}/api/v2/iip-client/restaurants?ids[]=${restaurantId}`);
	return result.data;
};

const useStyles = makeStyles((theme) => ({
	container: {
		[theme.breakpoints.up('md')]: {
			background: '#F6F8F8',
			borderRadius: '32px',
		},
		padding: 0,
	},
	greenLink: {
		fontStyle: 'normal',
		fontWeight: 'bold',
		fontSize: '14px',
		lineHeight: '22px',
		color: '#00B588',
		cursor: 'pointer',
	},
	banner: {
		backgroundColor: '#D6F3EC',
		borderRadius: '8px',
		color: '#00B588',
		display: 'flex',
	},
	qrContainer: {
		borderRadius: '8px',
		border: '2px solid #E9EDEC',
		margin: 'auto',
		maxWidth: '500px',
	},
	restaurantTitle: {
		fontWeight: 'bold',
		fontSize: '32px',
		marginBottom: theme.spacing(1),
		lineHeight: '1.3',
	},
	categoryTitle: {
		fontWeight: 'bold',
		textTransform: 'uppercase',
		marginTop: theme.spacing(2),
	},
	paragraph: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		fontSize: '14px',
	},
}));

const formatTimeForOpenHours = (timeStr: string) => {
	if (!/^\d*:\d*(:\d*(\.\d*)?)?$/.test(timeStr)) return timeStr;
	const parts = timeStr.split(':');
	if (!(parts.length > 2)) return timeStr;
	return `${parts[0]}:${parts[1]}`;
};

const checkMobile = (userAgent: string | undefined) => {
	let agent;
	if (userAgent) {
		agent = userAgent;
	} else {
		agent = navigator.userAgent || navigator.vendor;
	}
	return /iPad|iPhone|iPod/.test(agent) && (typeof window === 'undefined' || !('MSStream' in window)) || /android/i.test(agent) || /windows phone/i.test(agent);
};

const GreenButton = withStyles({
	root: {
		cursor: 'pointer',
		backgroundColor: '#00b588',
		borderRadius: '20px',
		padding: '10px',
		maxWidth: '90%',
		margin: 'auto',
		minWidth: '181px',
		color: '#fff',
		borderColor: '#28a745',
		'&:hover': {
			backgroundColor: '#00b588ba',
		},
		marginTop: '20px',
		whiteSpace: 'nowrap',
	},
})(Button);

const BottomMenuBox = withStyles({
	root: {
		backgroundColor: '#D6F3EC',
		width: '100%',
		position: 'fixed',
		bottom: 0,
		padding: '10px 0',
		display: 'flex',
		justifyContent: 'center',
		margin: '0 !important',
	},
})(Box);

//@ts-expect-error
const BottomMenu = ({ restaurant }) => {
	const [ dialog, showDialog ] = useState(false);
	const router = useRouter();

	return (<>
		<BottomMenuBox m={0}>
			<Button onClick={() => showDialog(true)} variant={'text'}>Chci si objednat a na čas vyzvednout</Button>
		</BottomMenuBox>
		<Dialog onClose={() => showDialog(false)} open={dialog}>
			<DialogTitle>
				<Typography align={'center'} variant={'h5'}>Vybrali jste si z našeho menu?</Typography>
			</DialogTitle>
			<DialogContent>
				<Typography variant={'subtitle1'} align={'center'}>Objednávejte přes Qerko appku</Typography>
			</DialogContent>
			<DialogActions>
				<Box m={1} display='flex' justifyContent='center' width={1}>
					<GreenButton onClick={() => router.push(`https://qerko.com/~qr/restaurant-id/${restaurant.id}`)}>Chci si objednat přes Qerko</GreenButton>
				</Box>
			</DialogActions>
		</Dialog>
	</>);
};

const MenuRequest = ({ restaurantId, date, isMobile }: {restaurantId: string; date: string; isMobile: boolean }) => {
	const { data: menuData, isLoading: isMenuLoading } = useQuery([ 'menu', { restaurantId, date } ], getMenu);
	const { data: restaurantData, isLoading: isRestaurantLoading } = useQuery([ 'restaurant', { restaurantId } ], getRestaurant);

	return (<>
		<Container maxWidth={'xl'} style={{ padding: '0 20px 55px 20px' }}>
			{!isMenuLoading && !isRestaurantLoading && restaurantData[0] && <MenuRender
				menu={menuData}
				restaurant={restaurantData[0]}
				date={date}
				isMobile={isMobile}
			/>}
			{!isRestaurantLoading && restaurantData.length === 0 && <Box width={1} display={'flex'} justifyContent={'center'}>
				<Box position={'absolute'} top={'49%'}>
					<Typography variant={'h4'} align={'center'}>Omlouváme se, ale restaurace nebyla nalezena</Typography>
				</Box>
			</Box>}
		</Container>
		{isMobile && restaurantData[0] && <BottomMenu
			restaurant={restaurantData[0]}
		/>}
	</>);
};

//@ts-expect-error
const MenuRender = ({ menu, restaurant, date, isMobile }) => {
	const styles = useStyles();
	const openingHours = useMemo(() => {
		const numberDay = parseInt(format(new Date(date), 'i'));
		return restaurant.openingHours
			//@ts-expect-error
			.filter((hours) => hours.day === numberDay)
			//@ts-expect-error
			.map((hour) => `${formatTimeForOpenHours(hour.openFrom)}\u00A0-\u00A0${formatTimeForOpenHours(hour.openTo)}`).join(', ');
	}, [ date, restaurant.openingHours ]);
	const [ day, nextDay ] = useMemo(() => {
		const day = format(new Date(), 'y-MM-dd') === date ? 'Dnes' : format(new Date(date), 'd.M.');
		const nextDay = format(add(new Date(date), { days: 1 }), 'y-MM-dd');
		return [ day, nextDay ];
	}, [ date ]);
	const emptyMenu = useMemo(() => {
		return !menu.dailyMenuData.some((dailyMenu: {data: Array<unknown>}) => dailyMenu.data.length) && menu.permanentMenuData.length === 0;
	}, [ menu.dailyMenuData, menu.permanentMenuData.length ]);
	return (
		<Grid container direction={'row-reverse'} justify={'center'}>
			{(!isMobile) && <QrCode restaurant={restaurant} />}
			<Grid item sm={12} lg={isMobile ? 10 : 9}>
				<Box className={styles.container} display={'flex'} alignItems={'center'}>
					<Grid container alignItems={'center'} justify={'center'}>
						<Grid item sm={8} md={8} xs={12} xl={6}>
							<Grid item container direction={'column'}>
								<Grid item style={{ margin: '30px 0' }}>
									<Box position={'relative'} display={'flex'} alignItems={'center'}>
										<Hidden smDown={true}>
											<Box position={'absolute'} top={0}>
												<Box position={'relative'} left={'-115%'} width={120} height={120} display={'flex'} justifyContent={'center'} alignItems={'center'} style={{ border: '2px solid #E9EDEC', borderRadius: '8px', padding: '4px' }}>
													<Box>
														<Avatar style={{ width: '100%', height: '100%' }} src={`${apiUrl}/api/v2/iip-client/restaurant/${restaurant.id}/avatar`} variant="rounded" />
													</Box>
												</Box>
											</Box>
										</Hidden>
										<Hidden mdUp={true}>
											<Box display={'flex'} justifyContent={'center'} alignItems={'center'} height={60} width={60} mr={1}>
												<Avatar style={{
													maxWidth: '60px',
													maxHeight: '60px',
													width: 'max-content',
													height: 'max-content',
													border: '2px solid #E9EDEC',
													borderRadius: '8px',
													padding: '3px',
												}} src={`${apiUrl}/api/v2/iip-client/restaurant/${restaurant.id}/avatar`} variant="rounded" />
											</Box>
										</Hidden>
										<Typography className={styles.restaurantTitle}>
											{restaurant.name}
										</Typography>
									</Box>
									<Box>
										<Grid container direction={'column'}>
											<Grid item container xs={'auto'} wrap={'nowrap'} justify={'space-between'}>
												<Grid item>
													<Typography variant={'body1'} >
														{restaurant.address}
													</Typography>
												</Grid>
												<Grid item>
													<Typography variant={'body1'} align={'right'} noWrap>
														<Link className={styles.greenLink} href={`https://www.google.com/maps/place/${restaurant.latitude},${restaurant.longitude}`}>Otevřít na Google Maps</Link>
													</Typography>
												</Grid>
											</Grid>
											<Grid item container xs={'auto'} wrap={'nowrap'} justify={'space-between'} alignItems={'center'}>
												<Grid item>
													<Typography variant={'body1'} noWrap={false}>
														{openingHours === '' ? '' : `${day} otevřeno ${openingHours}`}
													</Typography>
												</Grid>
												<Grid item >
													<Typography variant={'body1'} align={'right'} noWrap style={{ marginLeft: '10px' }}>
														<NextLink href={{ pathname: `/${restaurant.id}`, query: { date: nextDay } }} passHref={true}>
															<Link className={styles.greenLink}>Další dny</Link>
														</NextLink>
													</Typography>
												</Grid>
											</Grid>
										</Grid>
									</Box>
								</Grid>
								{emptyMenu && <Box my={10}>
									<Typography variant={'h3'} align={'center'}> Restaurace nemá vyplněné menu </Typography>
								</Box>}
								{//@ts-expect-error
									menu.dailyMenuData !== null && menu.dailyMenuData.map((dailyMenuData) => (dailyMenuData.data.map((category) => (
										<React.Fragment key={category.id}>
											<Grid item>
												<Box my={2}>
													<Typography variant='h6' className={styles.categoryTitle}>
														{category.name} <small>({category.openFrom.substr(0, 5)} - {category.openTo.substr(0, 5)})</small>
													</Typography>
													{category.description !== null && (<Typography>{category.description}</Typography>)}
												</Box>
											</Grid>
											{//@ts-expect-error
												category.items.map((item, index) => (
													<MenuRow
														key={`${category.id}${item.id}${index}`}
														item={item}
													/>
												))
											}
										</React.Fragment>
									))))
								}
								{//@ts-expect-error
									menu.permanentMenuData !== null && menu.permanentMenuData.map((category) => (
										<React.Fragment key={category.id}>
											<Grid item>
												<Box my={2}>
													<Typography variant='h6' className={styles.categoryTitle}>
														{category.name} <small>({category.openFrom.substr(0, 5)} - {category.openTo.substr(0, 5)})</small>
													</Typography>
													{category.description !== null && (<Typography>{category.description}</Typography>)}
												</Box>
											</Grid>
											{//@ts-expect-error
												category.items.map((item, index) => (
													<MenuRow
														key={`${category.id}${item.id}${index}`}
														item={item}
													/>
												))}
										</React.Fragment>
									))
								}
							</Grid>
						</Grid>
					</Grid>
				</Box>
			</Grid>
		</Grid>
	);
};

//@ts-expect-error
const MenuRow = ({ item }) => {
	const styles = useMenuRowStyles();

	return (
		<Box mb={2} >
			<Grid item container justify={'space-between'} wrap={'nowrap'}>
				<Grid item>
					<Typography variant={'h6'} className={styles.title}>
						{item.name}
					</Typography>
					<Typography variant={'body1'} className={styles.description}>
						{item.description}
					</Typography>
					<Typography variant={'h6'} className={styles.price}>
						{item.price} Kč
					</Typography>
				</Grid>
				<Grid item>
					{item.images.length > 0 && <Avatar style={{ maxWidth: '100%', width: '80px', height: '80px', border: '2px solid #E9EDEC', borderRadius: '8px' }} src={`${item.images[0]}:resize/80x80.jpg`} variant="rounded" />}
				</Grid>
			</Grid>
		</Box>
	);
};

//@ts-expect-error
const QrCode = ({ restaurant }) => {
	const styles = useStyles();
	const router = useRouter();
	return (
		<Grid item lg={3} md={5} sm={6} container direction={'column'} xs={12}>
			<Box key='a' display={'flex'} flexDirection={'column'} justifyContent={'center'} mt={5} maxHeight={900} height={1}>
				<Box key='b' px={3} display={'flex'} flexDirection={'column'} maxHeight={500} justifyContent={'center'}>
					<Typography variant={'h5'} align={'center'}>
						Vybrali jste si z našeho menu?
					</Typography>
					<Typography variant={'h6'} align={'center'}>
						Naskenujte QR kód a objednávejte přes Qerko appku.
					</Typography>
					<Box className={styles.qrContainer} display={'flex'} p={6} justifyContent={'center'} my={3}>
						<Box>
							<img height={150} style={{ maxWidth: '160px', margin: 'auto' }} src={`${process.env.NEXT_PUBLIC_QR_CODE_GENERATOR_URL}/?theme=logo&code=https%3A%2F%2Fqerko.com%2F~qr%2Frestaurant-id%2F${restaurant.id}&scale=3`} alt={''} />
						</Box>
					</Box>
					<Box my={2} style={{ padding: 0 }} >
						<Grid style={{ padding: 0 }} container justify={'space-evenly'} alignItems={'center'}>
							<Grid item>
								<Link href={'https://play.google.com/store/apps/details?id=com.itispaid&hl=cs'}>
									<img style={{ maxWidth: '100%' }} height={37} src={`${router.basePath}/img/google_play_badge_cs.png`} alt={'Google play'} />
								</Link>
							</Grid>
							<Grid item>
								<Link href={'https://apps.apple.com/us/app/qerko/id1363182258?itsct=apps_box&itscg=30200'}>
									<img style={{ maxWidth: '100%' }} height={37} src={`${router.basePath}/img/app_store_badge_cs.png`} alt={'App store'} />
								</Link>
							</Grid>
						</Grid>
					</Box>
				</Box>
			</Box>
		</Grid>
	);
};

export default MenuPage;

export const getServerSideProps: GetServerSideProps = async (props) => {
	const { query, req } = props;
	let { date = format(startOfToday(), 'yyyy-MM-dd'), restaurantId = '' } = query;

	if (Array.isArray(restaurantId)) {
		restaurantId = restaurantId.join(';');
	}

	if (Array.isArray(date)) {
		date = date.join(';');
	}

	const queryClient = new QueryClient();
	await queryClient.prefetchQuery([ 'menu', { restaurantId, date } ], getMenu);
	await queryClient.prefetchQuery([ 'restaurant', { restaurantId } ], getRestaurant);
	const dehydratedState = dehydrate(queryClient);

	return {
		props: {
			dehydratedState,
			restaurantId,
			date,
			userAgent: req.headers['user-agent'] || undefined,
		},
	};
};
